import React, { useEffect, useState, useContext, Dispatch } from 'react'
import {
  Accordion,
  Button,
  CheckboxProps,
  Form,
  Header,
  Icon,
  InputOnChangeData,
  Message,
  Segment,
} from 'semantic-ui-react'
import { IProject, QualifyingQuestionType, ProjectQuestionResponse, IQuestionCompareResult } from '../../../api/Data'
import CustomLabel from '../../../components/common/custom-label/CustomLabel'
import Data from '../../../api/Data'
import { GlobalContext } from '../../../contexts/GlobalContext'
import './CollectTabQualifyingQuestionsPage.scss'
import LinkDatasetsModal from '../../../components/modals/LinkDatasetsModal'
import { maxLength } from '../../../config/constants'
import { Tooltip } from '@mui/material'
import { RepeatOne, Label } from '@mui/icons-material'
import { HelpDocsLink } from '../../../components/common/ExternalLink'

const CollectTabQualifyingQuestionsPage = (props: IProps) => {
  const MAX_QUESTIONS_CHARACTER_LENGTH = 260
  // Props
  const { project, projectQuestions, setProjectQuestions, fetchProjectQuestions, handleChange } = props
  const { displaySetting } = project
  const { dispatchGlobalContext } = useContext(GlobalContext)

  const [originalQuestions, setOriginalQuestions] = useState<ProjectQuestionResponse[]>(
    JSON.parse(JSON.stringify(projectQuestions))
  )
  const [error, setError] = useState<string | null>(null)
  const [optionInputs, setOptionInputs] = useState<string[]>([])
  const [deletedQuestionIds, setDeletedQuestionIds] = useState<number[]>([])
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [activeIndices, setActiveIndices] = useState<{ [key: number]: boolean }>({})

  const compareQuestions = (
    currentQuestion: ProjectQuestionResponse,
    originalQuestionsParam: ProjectQuestionResponse[]
  ): IQuestionCompareResult => {
    // If ID is 0, it's a new question
    if (currentQuestion.id === 0) return { isChanged: true, isNew: true }

    // Find the original question by ID
    const originalQuestion = originalQuestionsParam.find((q) => q.id === currentQuestion.id)

    // If no original found, it's considered new
    if (!originalQuestion) return { isChanged: true, isNew: true }

    // Compare all properties to detect changes
    const isChanged =
      currentQuestion.text !== originalQuestion.text ||
      currentQuestion.alias !== originalQuestion.alias ||
      currentQuestion.type !== originalQuestion.type ||
      currentQuestion.datasetId !== originalQuestion.datasetId ||
      JSON.stringify(currentQuestion.options) !== JSON.stringify(originalQuestion.options) ||
      currentQuestion.isConvertToTag !== originalQuestion.isConvertToTag ||
      currentQuestion.isAskedPerClip !== originalQuestion.isAskedPerClip

    return { isChanged, isNew: false }
  }

  const hasUnsavedChanges = () => {
    if (deletedQuestionIds.length > 0) return true

    if (projectQuestions.length !== originalQuestions.length) return true

    for (let i = 0; i < projectQuestions.length; i++) {
      const { isChanged } = compareQuestions(projectQuestions[i], originalQuestions)
      if (isChanged) return true
    }

    dispatchGlobalContext({ type: 'set user has made changes', payload: false })
    return false
  }

  const isFormValid = () => {
    const errors: string[] = []

    for (let i = 0; i < projectQuestions.length; i++) {
      const question = projectQuestions[i]

      if (!question.text || question.text.trim() === '') {
        errors.push(`${i}-text`)
      }

      if (question.alias) {
        if (question.alias.includes('.')) errors.push(`${i}-label-has-dot`)
        if (question.alias.includes(' ')) errors.push(`${i}-label-has-space`)

        if (projectQuestions.some((q, idx) => q.alias === question.alias && idx !== i)) {
          errors.push(`${i}-label-duplicate-alias`)
        }
      }

      if (['SingleSelect', 'MultiSelect'].includes(question.type)) {
        const hasOptions = question.options && question.options.length > 0
        const hasDataset = question.datasetId && question.datasetId > 0

        if (!hasOptions && !hasDataset) {
          errors.push(`${i}-options`)
        }
      }
    }

    setFormErrors(errors)
    return errors.length === 0
  }

  const saveQuestions = async (skipRefresh = false) => {
    if (!project.id) return false
    dispatchGlobalContext({ type: 'set loading', payload: true })

    try {
      const questionsToUpdate = projectQuestions
        .map((q, index) => ({
          ...q,
          sortOrder: index,
        }))
        .filter((currentQuestion) => {
          const { isChanged } = compareQuestions(currentQuestion, originalQuestions)
          return isChanged
        })
        .map((q) => {
          if (q.id === 0) {
            const { id, ...rest } = q
            return rest
          }
          return q
        })

      // delete any questions first to avoid sortOrder clashes when updating
      if (deletedQuestionIds.length > 0) {
        const { error: deleteError } = await Data.deleteProjectQuestions(project.id, deletedQuestionIds)

        if (deleteError) {
          if (deleteError.response && deleteError.response.data) {
            const errorMessage = deleteError.response.data

            if (errorMessage.includes('not authorized') || errorMessage.includes('Project not found')) {
              setError('Something went wrong with your session. Please logout and back in before trying again.')
            } else {
              setError(`Error: ${errorMessage}`)
            }
          } else {
            setError(`An error occurred. Please try again later.`)
          }
          console.error('Error deleting questions:', deleteError)
          dispatchGlobalContext({ type: 'set loading', payload: false })
          dispatchGlobalContext({ type: 'set user has made changes', payload: false })
          return false
        }
      }

      if (questionsToUpdate.length > 0) {
        const { error: putError } = await Data.updateProjectQuestions(project.id, questionsToUpdate)

        if (putError) {
          if (putError.response && putError.response.data) {
            const errorMessage = putError.response.data

            if (errorMessage.includes('not authorized') || errorMessage.includes('Project not found')) {
              setError('Something went wrong with your session. Please logout and back in before trying again.')
            } else if (
              errorMessage.includes('not well-formed') ||
              errorMessage.includes('Data for the questions is missing') ||
              errorMessage.includes('Question not found')
            ) {
              setError('Something went wrong transforming your data. Please contact support.')
            } else {
              setError(`Error: ${errorMessage}`)
            }
          } else {
            setError(`An error occurred. Please try again later.`)
          }
          console.error('Error updating questions:', putError)
          dispatchGlobalContext({ type: 'set loading', payload: false })
          dispatchGlobalContext({ type: 'set user has made changes', payload: false })
          return false
        }
      }

      if ((questionsToUpdate.length === 0 && deletedQuestionIds.length === 0) || skipRefresh) {
        dispatchGlobalContext({ type: 'set loading', payload: false })
        dispatchGlobalContext({ type: 'set user has made changes', payload: false })
        return true
      }

      return await refreshQuestions()
    } catch (err) {
      console.error('Error during question operations:', err)
      dispatchGlobalContext({ type: 'set loading', payload: false })
      return false
    }
  }

  const refreshQuestions = async () => {
    const { questions: freshQuestions, error: fetchError } = await fetchProjectQuestions()

    if (fetchError) {
      console.error('Error refreshing questions:', fetchError)
      dispatchGlobalContext({ type: 'set loading', payload: false })
      return false
    }

    setProjectQuestions(freshQuestions)
    setOriginalQuestions(JSON.parse(JSON.stringify(freshQuestions)))
    setDeletedQuestionIds([])

    dispatchGlobalContext({ type: 'set user has made changes', payload: false })
    dispatchGlobalContext({ type: 'set loading', payload: false })
    return true
  }

  useEffect(() => {
    if (window && typeof window !== 'undefined') {
      // @ts-ignore
      window.collectTabQualifyingQuestionsHandlers = {
        isFormValid,
        hasUnsavedChanges,
        saveQuestions,
      }
    }

    // Cleanup function
    return () => {
      if (window && typeof window !== 'undefined') {
        // @ts-ignore
        delete window.collectTabQualifyingQuestionsHandlers
      }
    }
  })

  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (formErrors.includes(`${index}-text`)) {
      setFormErrors(formErrors.filter((err) => err !== `${index}-text`))
    }

    const { value } = e.target
    const updatedQuestions = [...projectQuestions]
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      text: value,
    }
    setProjectQuestions(updatedQuestions)
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const handleQuestionAliasChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (formErrors.includes(`${index}-label`)) {
      setFormErrors(formErrors.filter((err) => err !== `${index}-label`))
    }

    const { value } = e.target
    const updatedQuestions = [...projectQuestions]
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      alias: value,
    }
    setProjectQuestions(updatedQuestions)
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const handleQuestionTypeChange = (index: number, type: QualifyingQuestionType) => {
    if (type === 'FreeText' && formErrors.includes(`${index}-options`)) {
      setFormErrors(formErrors.filter((err) => err !== `${index}-options`))
    }

    const updatedQuestions = [...projectQuestions]
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      type,
      options: type === 'FreeText' ? [] : updatedQuestions[index].options || [],
    }
    setProjectQuestions(updatedQuestions)
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const syncOptionInputState = (e: React.ChangeEvent<HTMLInputElement>, questionIndex: number) => {
    const { value } = e.target
    const newOptionInputs = [...optionInputs]
    newOptionInputs[questionIndex] = value
    setOptionInputs(newOptionInputs)
  }

  const handleOptionInputSubmit = (e: React.KeyboardEvent<HTMLInputElement>, questionIndex: number) => {
    if (e.key === 'Enter' && optionInputs[questionIndex]) {
      const newOption = optionInputs[questionIndex].trim()
      const updatedQuestions = [...projectQuestions]
      const existingOptions = updatedQuestions[questionIndex].options || []

      if (existingOptions.includes(newOption)) {
        return
      }

      updatedQuestions[questionIndex].options = [...existingOptions, newOption]
      setProjectQuestions(updatedQuestions)

      if (formErrors.includes(`${questionIndex}-options`)) {
        setFormErrors(formErrors.filter((err) => err !== `${questionIndex}-options`))
      }
      dispatchGlobalContext({ type: 'set user has made changes', payload: true })

      const newOptionInputs = [...optionInputs]
      newOptionInputs[questionIndex] = ''
      setOptionInputs(newOptionInputs)
    }
  }

  const handleRemoveQuestionOption = (questionIndex: number, optionIndex: number) => {
    const updatedQuestions = [...projectQuestions]
    const options = updatedQuestions[questionIndex].options || []
    updatedQuestions[questionIndex].options = options.filter((_, idx) => idx !== optionIndex)
    setProjectQuestions(updatedQuestions)
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const handleAddQualifyingQuestion = () => {
    if (projectQuestions.length > 0) {
      const lastQuestion = projectQuestions[projectQuestions.length - 1]
      if (!lastQuestion.text || lastQuestion.text.trim() === '') {
        const lastQuestionIndex = projectQuestions.length - 1
        setFormErrors((prevErrors) => {
          if (!prevErrors.includes(`${lastQuestionIndex}-text`)) {
            return [...prevErrors, `${lastQuestionIndex}-text`]
          }
          return prevErrors
        })

        setActiveIndices((prevState: Record<number, boolean>) => ({
          ...prevState,
          [lastQuestionIndex]: true,
        }))
        return
      }
    }

    const newQuestionIndex = projectQuestions.length
    const newQuestion: ProjectQuestionResponse = {
      id: 0,
      text: '',
      type: 'SingleSelect',
      options: [],
      isConvertToTag: true,
      isAskedPerClip: false,
      sortOrder: newQuestionIndex,
    }

    setProjectQuestions([...projectQuestions, newQuestion])
    setActiveIndices((prevState: Record<number, boolean>) => ({
      ...prevState,
      [newQuestionIndex]: true,
    }))
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const handleRemoveQualifyingQuestion = (index: number) => {
    const questionToRemove = projectQuestions[index]

    if (questionToRemove.id !== 0) {
      setDeletedQuestionIds([...deletedQuestionIds, questionToRemove.id])
    }

    const updatedQuestions = projectQuestions.filter((_, idx) => idx !== index)
    setProjectQuestions(updatedQuestions)
    const updatedOptionInputs = [...optionInputs]
    updatedOptionInputs.splice(index, 1)
    setOptionInputs(updatedOptionInputs)

    setFormErrors(formErrors.filter((err) => !err.startsWith(`${index}-`)))
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const handleLinkDataset = (questionIndex: number) => {
    setCurrentQuestionIndex(questionIndex)
    setIsModalOpen(true)
  }

  const handleUnlinkDataset = (questionIndex: number) => {
    const updatedQuestions = [...projectQuestions]
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      datasetId: undefined,
      datasetTitle: undefined,
      datasetKeys: undefined,
    }
    setProjectQuestions(updatedQuestions)
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const handleDatasetSelect = (datasetId: number, datasetTitle: string) => {
    if (currentQuestionIndex !== -1) {
      const updatedQuestions = [...projectQuestions]
      updatedQuestions[currentQuestionIndex] = {
        ...updatedQuestions[currentQuestionIndex],
        datasetId,
        datasetTitle,
        options: [],
      }
      setProjectQuestions(updatedQuestions)

      if (formErrors.includes(`${currentQuestionIndex}-options`)) {
        setFormErrors(formErrors.filter((err) => err !== `${currentQuestionIndex}-options`))
      }
      dispatchGlobalContext({ type: 'set user has made changes', payload: true })
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setCurrentQuestionIndex(-1)
  }

  const handleCheckboxChange = (e: React.MouseEvent<HTMLInputElement, MouseEvent>, data: CheckboxProps) => {
    const { name, checked } = data

    const newProjectOption = { ...project.displaySetting, [String(name)]: Boolean(checked) }

    handleChange(e, {
      name: 'displaySetting',
      value: { ...newProjectOption },
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { name, value } = data

    const newProjectOption = { ...project.displaySetting, [name]: value }

    handleChange(e, {
      name: 'displaySetting',
      value: { ...newProjectOption },
    })
  }

  const toggleAccordion = (index: number) => {
    setActiveIndices((prevState: Record<number, boolean>) => ({
      ...prevState,
      [index]: !prevState[index],
    }))
  }

  const handleTagIconClick = (questionIndex: number) => {
    const updatedQuestions = [...projectQuestions]

    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      isConvertToTag: !updatedQuestions[questionIndex].isConvertToTag,
    }

    setProjectQuestions(updatedQuestions)
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  const handleRepeatIconClick = (questionIndex: number) => {
    const updatedQuestions = [...projectQuestions]

    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      isAskedPerClip: !updatedQuestions[questionIndex].isAskedPerClip,
    }

    setProjectQuestions(updatedQuestions)
    dispatchGlobalContext({ type: 'set user has made changes', payload: true })
  }

  // #region Render Functions
  const renderOptionsTitle = (question: ProjectQuestionResponse) => {
    if (question.datasetId)
      return (
        <>
          Linked Dataset
          <CustomLabel>
            <CustomLabel.Popup
              popupContent={
                <>
                  The only valid values for answers to this question will now be limited to <i>the key column values</i>{' '}
                  of the linked dataset.
                </>
              }
            />
          </CustomLabel>
        </>
      )
    if (question.options && question.options.length > 0) return 'Add options manually'
    return 'Add options manually or link Dataset'
  }

  const renderManualOptionsChips = (question: ProjectQuestionResponse, questionIndex: number) => {
    if (!question.options || question.options.length === 0) return null

    return (
      <div className="qualifying-questions__options-display">
        {question.options.map((option, index) => (
          <span key={index} className="qualifying-questions__option-tag">
            {option}
            <Button
              type="button"
              size="large"
              circular
              icon="close"
              onClick={() => handleRemoveQuestionOption(questionIndex, index)}
              className="qualifying-questions__remove-option-button"
            />
          </span>
        ))}
      </div>
    )
  }
  // #endregion

  return (
    <>
      {error && (
        <Message negative style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Message.Header>{error}</Message.Header>
        </Message>
      )}
      <Segment>
        <div className="qualifying-questions__header-container">
          <Header as="h2" className="qualifying-questions__header">
            Structured Contributor Fields
            <Header.Subheader>
              <p>
                Use this section to collect structured data from contributors—like location, department, store
                number, or topic. These fields support either manually entered response options or linked datasets
                for auto-completion.
              </p>
            </Header.Subheader>
          </Header>

          <Button type="button" onClick={handleAddQualifyingQuestion}>
            Add a Question
          </Button>
        </div>

        <Form>
          {/* Rating */}
          <Form.Field id='contributor-field'>
            <CustomLabel>
              <CustomLabel.Text text="Ask your contributor for a rating" />
              <CustomLabel.WordCount
                word={displaySetting.ratingPrompt}
                limit={maxLength.ratingPrompt}
              ></CustomLabel.WordCount>
              <CustomLabel.Checkbox
                name="showRating"
                checked={displaySetting.showRating}
                onClick={handleCheckboxChange}
              ></CustomLabel.Checkbox>
            </CustomLabel>
            <Form.Input
              placeholder="i.e. Rate our product from 1 to 5"
              maxLength={maxLength.ratingPrompt}
              name="ratingPrompt"
              value={displaySetting.ratingPrompt || ''}
              className={!displaySetting.showRating ? 'hidden-contributor-field' : ''}
              onChange={handleInputChange}
            />
          </Form.Field>
          {projectQuestions.length > 0 &&
            projectQuestions.map((question, i) => (
              <Accordion className="qualifying-questions__accordion_container">
                <Accordion.Title
                  active={activeIndices[i] || false}
                  index={i}
                  onClick={() => toggleAccordion(i)}
                  className={`qualifying-questions__question-header ${
                    formErrors.some((err) => err.startsWith(`${i}-`))
                      ? 'qualifying-questions__question-header--error'
                      : ''
                  }`}
                >
                  <div className="qualifying-questions__title-left">
                    <span>Question {i + 1}</span>
                    <span className="qualifying-questions__character-count">
                      ({question.text ? question.text.length : 0}/{MAX_QUESTIONS_CHARACTER_LENGTH})
                    </span>
                  </div>
                  <div className="qualifying-questions__title-right">
                    <Tooltip
                      title={question.isAskedPerClip ? 'Ask for each clip' : 'Ask only once'}
                      placement="top"
                      arrow
                    >
                      <span
                        className={`qualifying-questions__tag-icon ${
                          question.isAskedPerClip ? 'qualifying-questions__tag-icon--active' : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRepeatIconClick(i)
                        }}
                      >
                        <RepeatOne fontSize="small" style={{ strokeWidth: '1px' }} />
                      </span>
                    </Tooltip>
                    <Tooltip title={'Auto-convert answers to tags'} placement="top" arrow>
                      <span
                        className={`qualifying-questions__tag-icon ${
                          question.isConvertToTag ? 'qualifying-questions__tag-icon--active' : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTagIconClick(i)
                        }}
                      >
                        <Label fontSize="medium" />
                      </span>
                    </Tooltip>
                    <Button
                      type="button"
                      icon="trash"
                      size="large"
                      className="qualifying-questions__transparent-button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveQualifyingQuestion(i)
                      }}
                    />
                    <Icon name={activeIndices[i] ? 'chevron up' : 'chevron down'} className="chevron" />
                  </div>
                </Accordion.Title>
                <Accordion.Content active={activeIndices[i] || false}>
                  <div className="qualifying-questions__form-container">
                    {/* Type Field */}
                    <div className="qualifying-questions__field-row">
                      <Form.Field className="qualifying-questions__field-label">
                        <CustomLabel>
                          <CustomLabel.Text text="Type" />
                          <CustomLabel.Popup popupContent="Single Select and Multi Select options will be rendered as radio buttons or checkboxes when there are 12 or fewer options" />
                        </CustomLabel>
                        <Form.Select
                          placeholder="Select question type"
                          options={[
                            { key: 'freetext', value: 'FreeText', text: 'Free Text' },
                            { key: 'singleselect', value: 'SingleSelect', text: 'Single Select' },
                            { key: 'multiselect', value: 'MultiSelect', text: 'Multi Select' },
                          ]}
                          value={question.type}
                          onChange={(e, { value }) => handleQuestionTypeChange(i, value as QualifyingQuestionType)}
                        />
                      </Form.Field>
                    </div>

                    {/* Text & Label Row */}
                    <div className="qualifying-questions__text-label-container">
                      <div className="qualifying-questions__text-field">
                        <Form.Field className="qualifying-questions__field-label">
                          <CustomLabel>
                            <CustomLabel.Text text="Text" />
                          </CustomLabel>
                          <Form.Input
                            placeholder="e.g. What’s your email address?"
                            name={`questionText-${i}`}
                            maxLength={MAX_QUESTIONS_CHARACTER_LENGTH}
                            value={question.text || ''}
                            onChange={(e) => handleQuestionTextChange(e, i)}
                            error={
                              formErrors.includes(`${i}-text`) && {
                                content: 'Question text is required',
                                pointing: 'above',
                              }
                            }
                          />
                        </Form.Field>
                      </div>
                      <div className="qualifying-questions__label-field">
                        <Form.Field className="qualifying-questions__field-label">
                          <CustomLabel>
                            <CustomLabel.Text text="Label" />
                            <CustomLabel.Popup popupContent="A short name or alias for this field, used across the platform. Keep it short and clear—for example, the question “What is your store number?” might have the label “store”." />
                          </CustomLabel>
                          <Form.Input
                            placeholder="e.g. email"
                            name={`questionLabel-${i}`}
                            value={question.alias || ''}
                            onChange={(e) => handleQuestionAliasChange(e, i)}
                            error={
                              (formErrors.includes(`${i}-label-has-dot`) && {
                                content: 'Alias cannot contain dot (.) character',
                                pointing: 'above',
                              }) ||
                              (formErrors.includes(`${i}-label-has-space`) && {
                                content: 'Alias cannot contain spaces',
                                pointing: 'above',
                              }) ||
                              (formErrors.includes(`${i}-label-duplicate-alias`) && {
                                content: 'Alias must be unique across all questions',
                                pointing: 'above',
                              })
                            }
                          />
                        </Form.Field>
                      </div>
                    </div>

                    {/* Options & Dataset Section */}
                    <Segment>
                      <Header as="h4" className="qualifying-questions__options-title">
                        Choose how contributors can answer
                        <CustomLabel>
                          <CustomLabel.Popup popupContent="Constraints are required for Single Select and Multi Select questions, but they can also be used for Free Text questions to enforce a “blind” white-list of valid values." />
                        </CustomLabel>
                        <Header.Subheader>
                          <p>
                            You can decide how structured you want the answers to be. 
                            Either type your own list of responses or use a dataset if you have longer or more complex lists. 
                            Read more <HelpDocsLink documentTag="/how-to-gather-contributor-data-in-vloggis-video-submission-software/">here</HelpDocsLink>
                          </p>
                        </Header.Subheader>
                      </Header>

                      <Header as="h5" className="qualifying-questions__options-title">
                        {renderOptionsTitle(question)}
                      </Header>

                      <Form.Field required={['SingleSelect', 'MultiSelect'].includes(question.type)}>
                        <div className="qualifying-questions__options-container">
                          <Form.Input
                            value={
                              question.datasetId
                                ? question.datasetTitle || 'Unknown Dataset'
                                : optionInputs[i] || ''
                            }
                            readOnly={!!question.datasetId}
                            placeholder="Add an option and press 'Enter'..."
                            className="qualifying-questions__options-input"
                            onChange={(e) => syncOptionInputState(e, i)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleOptionInputSubmit(e, i)}
                            error={
                              formErrors.includes(`${i}-options`) && {
                                content: 'At least one option is required or link a dataset',
                                pointing: 'above',
                              }
                            }
                          />
                          <Button
                            type="button"
                            icon="linkify"
                            content={question.datasetId ? 'Unlink Dataset' : 'Link Dataset'}
                            disabled={question.options && question.options.length > 0}
                            onClick={() => (question.datasetId ? handleUnlinkDataset(i) : handleLinkDataset(i))}
                          />
                        </div>
                        {renderManualOptionsChips(question, i)}
                      </Form.Field>
                    </Segment>
                  </div>
                </Accordion.Content>
              </Accordion>
            ))}
        </Form>
      </Segment>

      {/* Link Dataset Modal */}
      <LinkDatasetsModal isOpen={isModalOpen} onClose={handleCloseModal} onSelect={handleDatasetSelect} />
    </>
  )
}

interface IProps {
  project: IProject
  projectQuestions: ProjectQuestionResponse[]
  setProjectQuestions: Dispatch<ProjectQuestionResponse[]>
  fetchProjectQuestions: () => Promise<{
    questions: ProjectQuestionResponse[]
    error?: string | undefined
  }>
  handleChange: (
    arg: React.SyntheticEvent | null,
    payload: { name: string; value?: string; checked?: boolean; values?: string[] }
  ) => void
}

export default CollectTabQualifyingQuestionsPage
